import isVizio from '@accedo/xdk-device-vizio-smartcast-contrib/esm/detection'
import { Lightning } from '@lightningjs/sdk'

import { Registry } from '@lightningjs/sdk'
import { getColor, grey800 } from '../../constants/colors'
import { BORDER_RADIUS } from '../../constants/general'
import SpeechComp from '../speechComp/speechComp'
import { createKbLayout } from './keyboardUtils'
import KeyRow from './KeyRow'

export type SimpleKey = string
export type SpecialKey = { route: string; action: string; speechText: string }
export type KeyType = SimpleKey | SpecialKey

export default class CustomKeyboard extends SpeechComp {
  private _index = 0
  private _keyHandler = null

  static _template() {
    return {
      w: 380,
      h: 755,
      rect: true,
      color: getColor(grey800),
      shader: {
        type: Lightning.shaders.RoundedRectangle,
        radius: BORDER_RADIUS,
      },
    }
  }

  get sectionText() {
    return 'Keyboard'
  }

  get index() {
    return this._index
  }

  set index(i: number) {
    this._index = i
  }

  get currentRow() {
    return this.childList.getAt(this.index) as KeyRow
  }

  get totalRows() {
    return this.childList.length
  }

  get isFocusOnPenultimateRow() {
    return this._index === this.totalRows - 2
  }

  get isFocusOnLastRow() {
    return this._index === this.totalRows - 1
  }

  static _states() {
    return [
      class RegularBackHandler extends this {
        _handleBack() {
          return false
        }
      },
      class VizioBackHandler extends this {
        _handleBack() {
          return true
        }
      },
    ]
  }

  _init() {
    this.patch({ children: createKbLayout() })

    this._setState(isVizio ? 'VizioBackHandler' : 'RegularBackHandler')
  }

  _getFocused() {
    return this.currentRow
  }

  _focus() {
    if (isVizio) {
      this.attachVizioKbListener()
    }
  }

  _unfocus() {
    if (isVizio) {
      this.dettachVizioKbListener()
    }
  }

  _handleDown() {
    if (this.index === this.totalRows - 1) {
      return false
    } else {
      this.nextRow()
    }
  }

  _handleUp() {
    if (this.index === 0) {
      return false
    } else {
      this.prevRow()
    }
  }

  _handleEnter() {
    this.handleKey(this.currentRow.keyValue)
  }

  handleKey(value) {
    if (typeof value === 'string') {
      this.signal('onKeyPress', value)
      return
    }

    const { action } = value || {}
    switch (action) {
      case 'trash':
        this.signal('onClearPress')
        return
      case 'space':
        this.signal('onKeyPress', ' ')
        return
      case 'backspace':
        this.signal('onBackspacePress')
        return
      default:
        return
    }
  }

  nextRow() {
    const currentIndex = this.currentRow.keyIndex
    const nextRow = this.getRowByIndex(this.index + 1)

    if (this.isFocusOnPenultimateRow) {
      if (currentIndex === 0) {
        nextRow.keyIndex = 0
      } else if (currentIndex === 1 || currentIndex === 2) {
        nextRow.keyIndex = 1
      } else {
        nextRow.keyIndex = 2
      }
    } else {
      nextRow.keyIndex = currentIndex
    }

    this._index++
  }

  prevRow() {
    const currentIndex = this.currentRow.keyIndex
    const prevRow = this.getRowByIndex(this.index - 1)

    if (this.isFocusOnLastRow) {
      if (currentIndex === 0) {
        prevRow.keyIndex = 0
      } else if (currentIndex === 1) {
        prevRow.keyIndex = 1
      } else {
        prevRow.keyIndex = 3
      }
    } else {
      prevRow.keyIndex = currentIndex
    }

    this._index--
  }

  getRowByIndex(i: number) {
    return this.childList.getAt(i) as KeyRow
  }

  _handleClick() {
    this._handleEnter()
  }

  attachVizioKbListener() {
    this._keyHandler = (e: KeyboardEvent) => {
      if (e?.key === 'Backspace') {
        this.signal('onBackspacePress')
        e.stopImmediatePropagation()
        return
      }

      if (e.key === 'Space' || e.key === ' ') {
        this.signal('onKeyPress', ' ')
        return
      }

      // Prevent keyboard from mobile app to send sybmbols
      // Backend only allows lowercase letters and numbers
      const isAllowed = /^[A-Za-z0-9]$/.test(e.key)

      if (isAllowed) {
        this.signal('onKeyPress', e.key.toLowerCase())
      }
    }

    Registry.addEventListener(document, 'keydown', this._keyHandler)
  }

  dettachVizioKbListener() {
    if (this._keyHandler) {
      Registry.removeEventListener(document, 'keydown', this._keyHandler)
      this._keyHandler = null
    }
  }
}
